import { AUTH_COOKIE_DOMAIN, deleteCookieData, getCookieData, setCookieData } from '@repo/common-utils';
import { addBznavMessageListener, removeBznavMessageListener, sendBznavNativeMessage, useCommonRouter, useUTM } from '@repo/platform';
import { useDialog } from '@repo/ui';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ACCESS_TYPE, SIGN_REQUIRED_STEP_COOKIE_KEY } from '../constants';
import { AppLoginContextType, UserAuthBroadcastCookieMessage } from '../types';

import { useAuthContext } from './AuthProvider';

const AppSignInContext = React.createContext<AppLoginContextType | undefined>(undefined);

export const AppSignProvider = ({ children, signInUrl }: { children: React.ReactNode; signInUrl: string }) => {
  const router = useCommonRouter();
  const { authService, auth, setStatusSignIn, setStatusSignOut } = useAuthContext();
  const [signRequiredStep, setSignRequiredStep] = useState<AppLoginContextType['signRequiredStep']>({
    ciAuthentication: false,
    reAgreeTermKeys: []
  });
  const { showDialog } = useDialog();
  const { utmData } = useUTM();

  const stateFlag = useRef({ isSignInProgress: false, isSignInChecked: false });
  const autoLoginParameters = useRef({
    code: '',
    grantType: ''
  });
  const accessType = useRef('');
  const returnUrl = useRef('');
  const [isAppSignInProcedure, setIsAppSignInProcedure] = useState(false);
  const [isAppUserInitialized, setIsAppUserInitialized] = useState(false);

  const loginComplete = useCallback(() => {
    setStatusSignIn();
    setIsAppUserInitialized(true);
    setIsAppSignInProcedure(false);
    stateFlag.current.isSignInProgress = false;
    stateFlag.current.isSignInChecked = true;
    autoLoginParameters.current = {
      code: '',
      grantType: ''
    };
  }, [setStatusSignIn, setIsAppUserInitialized]);

  const resetSignRequiredStep = useCallback(() => {
    setSignRequiredStep({ ciAuthentication: false, reAgreeTermKeys: [] });
    deleteCookieData(SIGN_REQUIRED_STEP_COOKIE_KEY, { domain: AUTH_COOKIE_DOMAIN, path: '/' });
    sendBznavNativeMessage('broadcastWebviewCookies', {
      label: 'userCI',
      actionType: 'remove',
      targetApps: authService?.cookieBroadcastTargetApps,
      cookies: [
        {
          name: SIGN_REQUIRED_STEP_COOKIE_KEY,
          value: '',
          options: { domain: AUTH_COOKIE_DOMAIN, path: '/' }
        }
      ]
    });
  }, [authService?.cookieBroadcastTargetApps]);

  const syncBroadcastedCookies = useCallback((actionType: string, cookies: UserAuthBroadcastCookieMessage['cookies']) => {
    cookies.forEach((cookie) => {
      const cookieOptions = { ...cookie.options, expires: cookie.options.expires ? new Date(cookie.options.expires) : undefined };
      if (actionType === 'set') {
        setCookieData(cookie.name, cookie.value, cookieOptions);
      } else {
        deleteCookieData(cookie.name, cookieOptions);
      }
    });
  }, []);

  const updateCookieMessageHandler = useCallback(
    (messageData: unknown) => {
      const data = messageData as UserAuthBroadcastCookieMessage;

      if (data?.label === 'userAuth') {
        syncBroadcastedCookies(data.actionType, data.cookies);
        if (data?.actionType === 'set') {
          loginComplete();
        } else {
          setStatusSignOut();
        }
      } else {
        syncBroadcastedCookies(data.actionType, data.cookies);
      }
    },
    [loginComplete, setStatusSignOut, syncBroadcastedCookies]
  );

  const startAppLogin = useCallback(async () => {
    if (!authService) return;
    try {
      const { accessToken, user, successReturnUrl, requiredStep } = await authService.requestToken({
        code: autoLoginParameters.current.code,
        grantType: accessType.current !== 'normal' ? autoLoginParameters.current.grantType : undefined,
        utmData
      });
      if (!accessToken || !user) {
        throw new Error('Invalid access token or user data');
      }

      const url = new URL((window.location.origin + successReturnUrl) as string);
      returnUrl.current = url.pathname;
      loginComplete();

      if (requiredStep?.ciAuthentication) {
        setSignRequiredStep(requiredStep);
        setCookieData(SIGN_REQUIRED_STEP_COOKIE_KEY, requiredStep, { domain: AUTH_COOKIE_DOMAIN, path: '/' });
        sendBznavNativeMessage('broadcastWebviewCookies', {
          label: 'userCI',
          actionType: 'set',
          targetApps: authService?.cookieBroadcastTargetApps,
          cookies: [
            {
              name: SIGN_REQUIRED_STEP_COOKIE_KEY,
              value: JSON.stringify(requiredStep),
              options: { domain: AUTH_COOKIE_DOMAIN, path: '/' }
            }
          ]
        });
      } else {
        resetSignRequiredStep();
      }
    } catch (error: unknown) {
      console.error(error);
      showDialog({
        title: '로그인을 실패했어요',
        description: '보안상으로 유효하지 않은 링크로 접속하였어요.\n다시 직접 로그인을 해주세요.',
        buttons: {
          primary: {
            text: '로그인 하러가기',
            onClick: () => {
              setStatusSignOut(true);
              setIsAppSignInProcedure(false);
              stateFlag.current.isSignInProgress = false;
              stateFlag.current.isSignInChecked = true;
              autoLoginParameters.current = {
                code: '',
                grantType: ''
              };
              router.replace(signInUrl);
            }
          }
        }
      });
    }
  }, [authService, utmData, loginComplete, router, resetSignRequiredStep, showDialog, setStatusSignOut, signInUrl]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const autoLoginCode = queryParams.get('code');
    const grantType = queryParams.get('grant_type');

    const isStartSignIn = !!autoLoginCode && !!grantType && auth.status === 'checking';
    accessType.current = (grantType && ACCESS_TYPE[grantType]) || 'normal';
    setIsAppSignInProcedure(isStartSignIn);

    if (isStartSignIn && !stateFlag.current.isSignInProgress) {
      autoLoginParameters.current = { code: autoLoginCode, grantType };
      stateFlag.current.isSignInProgress = true;
      startAppLogin();
      return;
    }

    if (stateFlag.current.isSignInChecked || stateFlag.current.isSignInProgress) return;
    if (auth.apiToken) {
      setStatusSignIn();
    } else if (!autoLoginCode || !grantType) {
      setStatusSignOut();
    }
    stateFlag.current.isSignInChecked = true;
  }, [auth.apiToken, auth.status, setStatusSignIn, setStatusSignOut, startAppLogin]);

  useEffect(() => {
    addBznavMessageListener('updateCookies', updateCookieMessageHandler);

    return () => removeBznavMessageListener('updateCookies', updateCookieMessageHandler);
  }, [updateCookieMessageHandler]);

  useEffect(() => {
    const savedSignRequiredStep = getCookieData(SIGN_REQUIRED_STEP_COOKIE_KEY);
    if (savedSignRequiredStep) {
      setSignRequiredStep(savedSignRequiredStep);
    }
  }, []);

  return (
    <AppSignInContext.Provider
      value={{
        accessType: accessType.current,
        isAppUserInitialized,
        isAppSignInProcedure,
        signRequiredStep,
        resetSignRequiredStep,
        returnUrl: returnUrl.current
      }}
    >
      {children}
    </AppSignInContext.Provider>
  );
};

export const useAppSignInContext = () => {
  const ctx = useContext(AppSignInContext);
  if (!ctx) throw new Error('useAppLoginContext must be used within AppLoginProvider');
  return ctx;
};
